/**
 * Keeps a "Calgary Shipping Charge" fee line in sync with the cart whenever
 * the customer picks Calgary as their shipping location on the product page.
 *
 * The theme has no cart:update pub/sub events and two independent
 * add-to-cart code paths (product-form.js, dT_bundle_base.js), so instead of
 * patching each cart-mutating component, this wraps window.fetch once and
 * reconciles after any /cart/*.js call. This keeps every existing cart file
 * untouched.
 */
window.CalgaryShipping = (function () {
  var CONFIG_EL_ID = 'CalgaryShippingConfig';
  var FEE_PROPERTY = '_calgary_fee_size';
  var LOCATION_PROPERTY = 'Shipping Location';
  var CALGARY = 'Calgary';
  // Matches /cart/add, /cart/add.js, /cart/change.js, etc. — Shopify serves
  // both the .js and non-.js forms as the AJAX cart endpoint, and this store
  // calls the non-.js form, so the suffix can't be required.
  var MUTATING_ENDPOINT_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  // Serializes reconcile() calls so every caller genuinely waits for a full
  // pass over the current cart state, instead of a boolean mutex that let
  // concurrent callers (including the checkout-time `await`) resolve before
  // the real sync finished.
  var reconcileQueue = Promise.resolve();
  // Set around this module's own cart-mutating fetches so the window.fetch
  // patch below doesn't re-trigger a reconcile for changes it made itself.
  var isOwnRequest = false;

  function getConfig() {
    var el = document.getElementById(CONFIG_EL_ID);
    if (!el) {
      console.log('[CalgaryDebug] #CalgaryShippingConfig script tag not found in DOM at all');
      return null;
    }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.log('[CalgaryDebug] #CalgaryShippingConfig JSON failed to parse:', e, el.textContent);
      return null;
    }
  }

  function isShippingExpired(config) {

    if (!config.endDate) return false;

    // Visitor ke browser ka local time
    var now = new Date();

    // Backend date ka end time (visitor timezone)
    var expiry = new Date(config.endDate + "T23:59:59");

    return now > expiry;
  }

  function getSectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var found = doc.querySelector(selector);
    return found ? found.innerHTML : null;
  }

  function getSectionTargets() {
    var targets = [];

    var mainCartItemsEl = document.getElementById('main-cart-items');
    if (mainCartItemsEl && mainCartItemsEl.dataset.id) {
      targets.push({ elementId: 'main-cart-items', requestId: mainCartItemsEl.dataset.id, selector: '.js-contents' });
    }

    var mainCartFooterEl = document.getElementById('main-cart-footer');
    if (mainCartFooterEl && mainCartFooterEl.dataset.id) {
      targets.push({ elementId: 'main-cart-footer', requestId: mainCartFooterEl.dataset.id, selector: '.js-contents' });
    }

    if (document.getElementById('CartDrawer')) {
      targets.push({ elementId: 'CartDrawer', requestId: 'cart-drawer', selector: '#CartDrawer' });
    }

    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ elementId: 'cart-icon-bubble', requestId: 'cart-icon-bubble', selector: '.shopify-section' });
    }

    return targets;
  }

  function applySections(sections, targets) {
    targets.forEach(function (t) {
      var html = sections && sections[t.requestId];
      var el = document.getElementById(t.elementId);
      if (typeof html !== 'string' || !el) return;
      var targetEl = el.querySelector(t.selector) || el;
      var inner = getSectionInnerHTML(html, t.selector);
      // inner can legitimately be '' (e.g. cart just became empty) — only
      // skip the write when the selector wasn't found in the response at all.
      if (inner !== null) targetEl.innerHTML = inner;
    });
  }

  function sizeFromLine(line) {
    var found = (line.options_with_values || []).filter(function (o) {
      return o.name === 'Size';
    })[0];
    if (found) return found.value;
    // No Size variant option on this line's product — fall back to the
    // hidden _idol_size property set from its product tag (see
    // snippets/shipping-location-picker.liquid's fixed_size param).
    var props = line.properties || {};
    return props['_idol_size'] || null;
  }

  function computeTargetFees(cart) {
    var target = {};
    cart.items.forEach(function (line) {
      var props = line.properties || {};
      if (props[FEE_PROPERTY]) return;
      if (props[LOCATION_PROPERTY] !== CALGARY) return;
      var size = sizeFromLine(line);
      if (!size) return;
      target[size] = (target[size] || 0) + line.quantity;
    });
    return target;
  }

  function existingFeeLines(cart) {
    var bySize = {};
    cart.items.forEach(function (line) {
      var props = line.properties || {};
      var size = props[FEE_PROPERTY];
      if (!size) return;
      bySize[size] = bySize[size] || [];
      bySize[size].push(line);
    });
    return bySize;
  }

  // Marks the request as ours (so the fetch patch below ignores it) and
  // rejects on a failed cart mutation instead of treating any resolved
  // fetch as success — Shopify's cart API can return an error payload
  // (e.g. {status: 422, description: "..."}) with a non-throwing response.
  function postJSON(url, body) {
    isOwnRequest = true;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            var message = (data && (data.description || data.message)) || 'Cart request failed (' + res.status + ')';
            throw new Error(message);
          }
          return data;
        });
      })
      .finally(function () {
        isOwnRequest = false;
      });
  }

  function runOnce() {
    var didChange = false;

    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var config = getConfig();
        if (!config || !config.enabled || isShippingExpired(config)) {
          return null;
        }
        var target = computeTargetFees(cart);
        var existing = existingFeeLines(cart);
        var updates = {};
        var itemsToAdd = [];

        console.log('[CalgaryDebug] cart items:', cart.items.map(function (i) {
          return { title: i.product_title, properties: i.properties, options_with_values: i.options_with_values, quantity: i.quantity };
        }));
        console.log('[CalgaryDebug] config loaded:', config);
        console.log('[CalgaryDebug] target fees needed (size -> qty):', target);
        console.log('[CalgaryDebug] existing fee lines (size -> lines):', existing);

        Object.keys(existing).forEach(function (size) {
          existing[size].forEach(function (line, index) {
            var wantedQty = index === 0 ? target[size] || 0 : 0;
            if (wantedQty !== line.quantity) updates[line.key] = wantedQty;
          });
          if (target[size]) delete target[size];
        });

        Object.keys(target).forEach(function (size) {
          var qty = target[size];
          if (!qty) return;
          var variantId = config && config.variantsBySize && config.variantsBySize[size];
          if (!variantId) {
            console.log('[CalgaryDebug] NO variant id found in config.variantsBySize for size:', JSON.stringify(size), '- available keys:', config && config.variantsBySize && Object.keys(config.variantsBySize));
            return;
          }
          var props = {};
          props[FEE_PROPERTY] = size;
          itemsToAdd.push({ id: variantId, quantity: qty, properties: props });
        });

        console.log('[CalgaryDebug] updates to send:', updates);
        console.log('[CalgaryDebug] items to add:', itemsToAdd);

        didChange = Object.keys(updates).length > 0 || itemsToAdd.length > 0;
        console.log('[CalgaryDebug] didChange:', didChange);
        if (!didChange) return null;

        var chain = Promise.resolve();
        if (Object.keys(updates).length) {
          chain = chain.then(function () {
            return postJSON(window.routes.cart_update_url, { updates: updates });
          });
        }
        if (itemsToAdd.length) {
          chain = chain.then(function () {
            return postJSON(window.routes.cart_add_url, { items: itemsToAdd });
          });
        }
        return chain;
      })
      .then(function () {
        if (!didChange) return null;
        var targets = getSectionTargets();
        var requestIds = targets.map(function (t) { return t.requestId; }).filter(Boolean);
        if (!requestIds.length) return null;
        return postJSON(window.routes.cart_update_url, { updates: {}, sections: requestIds }).then(function (result) {
          applySections(result.sections, targets);
          toggleEmptyState(result.item_count === 0);
        });
      });
  }

  // Mirrors the is-empty toggling assets/cart.js does after its own
  // updates — needed here too since this module's own DOM writes bypass
  // that code path entirely, and would otherwise leave the "cart is empty"
  // panel hidden after a reconcile-triggered removal empties the cart.
  function toggleEmptyState(isEmpty) {
    var selectors = ['cart-items', 'cart-drawer-items', 'cart-drawer', '#main-cart-footer'];
    selectors.forEach(function (selector) {
      var el = document.querySelector(selector);
      if (el) el.classList.toggle('is-empty', isEmpty);
    });
  }

  // Every call is appended to the same queue so passes never overlap, and
  // each caller's own returned promise reflects that specific pass's
  // outcome (success or failure) rather than a shared, swallowed one — a
  // failed pass doesn't block passes queued after it.
  function reconcile() {
    var runPromise = reconcileQueue.then(runOnce);
    reconcileQueue = runPromise.catch(function () {});
    return runPromise.catch(function (err) {
      console.error('Calgary shipping sync failed:', err);
      throw err;
    });
  }

  function patchFetch() {
    var originalFetch = window.fetch;

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isMutating = MUTATING_ENDPOINT_PATTERN.test(url);

      var result = originalFetch.apply(this, arguments);

      if (isMutating && !isOwnRequest) {
        result
          .then(function () {
            setTimeout(function () {
              reconcile().catch(function () {});
            }, 50);
          })
          .catch(function () {});
      }

      return result;
    };
  }

  document.addEventListener('DOMContentLoaded', function () {
      var config = getConfig();

      if (!config || !config.enabled || isShippingExpired(config)) {
        return;
      }

      patchFetch();
      reconcile().catch(function () {});
  });

  return { reconcile: reconcile };
})();